import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../components/common/Button';
import { PlayingIconBtn } from './PlayingDisclosure';

function IconTakeback() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 14L4 9l5-5" />
      <path d="M4 9h10a5 5 0 0 1 0 10h-1" />
    </svg>
  );
}

function IconDraw() {
  return <span className="text-[11px] font-bold tracking-tight">½</span>;
}

function IconFlag() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <line x1="4" y1="22" x2="4" y2="15" />
    </svg>
  );
}

function IconAbort() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <line x1="9" y1="9" x2="15" y2="15" />
      <line x1="15" y1="9" x2="9" y2="15" />
    </svg>
  );
}

function IconReport() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

/**
 * Compact in-game actions (Lichess-style icons). Confirm / offer responses stay as buttons.
 */
export default function PlayingGameControls({
  busy,
  humanGame,
  plyCount,
  engineThinking,
  canAbort,
  gameMeta,
  confirmResign,
  confirmAbort,
  onCancelConfirm,
  onResign,
  onAbort,
  onDraw,
  onDeclineDraw,
  onClaimDraw,
  onTakeback,
  onDeclineTakeback,
  onReport,
  reportBusy,
  className = '',
}) {
  const { t } = useTranslation();

  if (confirmResign || confirmAbort) {
    return (
      <div className={`playing-icon-bar playing-icon-bar--confirm ${className}`.trim()}>
        <p className="w-full text-sm text-stone-600">
          {confirmResign ? t('playing.resignConfirm') : t('playing.abortConfirm')}
        </p>
        <Button variant="page-danger" disabled={busy} onClick={confirmResign ? onResign : onAbort}>
          {confirmResign ? t('playing.resign') : t('playing.abort')}
        </Button>
        <Button variant="page-secondary" onClick={onCancelConfirm}>
          {t('playing.cancel')}
        </Button>
      </div>
    );
  }

  if (gameMeta?.drawOfferedByOpponent) {
    return (
      <div className={`playing-icon-bar playing-icon-bar--confirm ${className}`.trim()}>
        <Button variant="page-primary" disabled={busy} onClick={onDraw}>
          {t('playing.acceptDraw')}
        </Button>
        <Button variant="page-secondary" disabled={busy} onClick={onDeclineDraw}>
          {t('playing.declineDraw')}
        </Button>
      </div>
    );
  }

  if (gameMeta?.takebackOfferedByOpponent) {
    return (
      <div className={`playing-icon-bar playing-icon-bar--confirm ${className}`.trim()}>
        <Button variant="page-primary" disabled={busy} onClick={onTakeback}>
          {t('playing.acceptTakeback')}
        </Button>
        <Button variant="page-secondary" disabled={busy} onClick={onDeclineTakeback}>
          {t('playing.declineTakeback')}
        </Button>
      </div>
    );
  }

  return (
    <div className={`playing-icon-bar ${className}`.trim()} role="toolbar" aria-label={t('playing.gameActions', { defaultValue: 'Game actions' })}>
      {plyCount > 0 && (
        <PlayingIconBtn
          label={gameMeta?.takebackOfferedByMe
            ? t('playing.waitingForOpponentTakeback', { defaultValue: 'Waiting for opponent…' })
            : t('playing.offerTakeback')}
          disabled={busy || engineThinking || !!gameMeta?.takebackOfferedByMe}
          onClick={onTakeback}
        >
          <IconTakeback />
        </PlayingIconBtn>
      )}
      {humanGame && (
        <PlayingIconBtn
          label={gameMeta?.drawOfferedByMe
            ? t('playing.waitingForOpponentDraw', { defaultValue: 'Waiting for opponent…' })
            : t('playing.offerDraw')}
          disabled={busy || !!gameMeta?.drawOfferedByMe}
          onClick={onDraw}
          active={!!gameMeta?.drawOfferedByMe}
        >
          <IconDraw />
        </PlayingIconBtn>
      )}
      {humanGame && gameMeta?.canClaimDraw && (
        <Button
          variant="page-primary"
          disabled={busy}
          onClick={onClaimDraw}
        >
          {t('playing.claimDraw', { defaultValue: 'Claim draw' })}
        </Button>
      )}
      <PlayingIconBtn
        label={t('playing.resign')}
        danger
        disabled={busy}
        onClick={onResign}
      >
        <IconFlag />
      </PlayingIconBtn>
      {canAbort && (
        <PlayingIconBtn
          label={t('playing.abort')}
          disabled={busy}
          onClick={onAbort}
        >
          <IconAbort />
        </PlayingIconBtn>
      )}
      {humanGame && (
        <PlayingIconBtn
          label={t('playing.reportPlayer', { defaultValue: 'Report' })}
          danger
          disabled={busy || reportBusy}
          onClick={onReport}
        >
          <IconReport />
        </PlayingIconBtn>
      )}
    </div>
  );
}
